import { HazardBaseLayer } from "./HazardBaseLayer";
import {
  getHazardLayerProps,
  type HazardLayerConfig,
} from "@/features/map/utils/hazardLayerUtils";
import { PolygonSymbolizer, type PaintRule } from "protomaps-leaflet";
import { useMemo } from "react";

interface Props {
  year: number;
  defaultVisible: boolean;
  visible?: boolean;
}

const host = process.env.VITE_HAZARD_TILE_HOST ?? "";

// 下記の文字列は年を分けるラジオボタンのグループの識別でも使われるため、特殊な変な文字を使わないでください
export const LANDSLIDE_PREVENTION_LAYER_NAME = "地すべり防止地区";

const layerConfig: HazardLayerConfig = {
  // leafletが管理するレイヤー名（実際のleafletレイヤー名はここに年度サフィックスを付加したもの）
  layerName: LANDSLIDE_PREVENTION_LAYER_NAME,
  yearConfigs: {
    2021: {
      url: `${host}/46/2021_landslide_prevention_46.pmtiles`,
      dataLayer: "2021_4326_merged",
    },
    2020: {
      url: `${host}/46/2020_landslide_prevention_46.pmtiles`,
      dataLayer: "2020_4326_merged",
    },
  },
};

// 色分け不要のため1色で塗る
// 塗り色は不透明にし、レイヤーレベルのopacityで透過させる
// （ピクセル自体にalpha値を持たせるとズーム時に旧・新タイルの重なりで一瞬濃くなる）
const FILL_COLOR = "#d95684";
const LAYER_OPACITY = 0.3;

export const LANDSLIDE_PREVENTION_MIN_ZOOM = 14;
export const LANDSLIDE_PREVENTION_MAX_ZOOM = 22;
const MIN_ZOOM = LANDSLIDE_PREVENTION_MIN_ZOOM;
const MAX_ZOOM = LANDSLIDE_PREVENTION_MAX_ZOOM;

// colorMapは使用しないが、HazardBaseLayerのPropsで必須のため空オブジェクトを定数として定義
// （オブジェクトリテラルを直接渡すと毎回新しい参照が生成され、useEffectが発火してチカチカする）
const EMPTY_COLOR_MAP: Record<string, string> = {};

/**
 * 年度ごとの paintRules を生成する
 * dataLayer 名が年度によって異なるため、動的に生成する必要がある
 */
const createPaintRules = (dataLayer: string): PaintRule[] => [
  {
    dataLayer,
    symbolizer: new PolygonSymbolizer({
      fill: FILL_COLOR,
      opacity: 1,
    }),
    minzoom: MIN_ZOOM,
    maxzoom: MAX_ZOOM,
  },
];

/*
地すべり防止地区レイヤー（A46）
タイル元データ: 地すべり防止区域データ 2020年度, 2021年度
S3バケット: lakers-saas-(env)-hazard-tile
パス: 46/2021_landslide_prevention_46.pmtiles, 46/2020_landslide_prevention_46.pmtiles
*/
export const LandslidePreventionLayer: React.FC<Props> = ({
  defaultVisible,
  year,
  visible,
}) => {
  const layerProps = getHazardLayerProps(year, layerConfig);

  // paintRulesの参照を安定させる（毎回新規生成するとHazardBaseLayerのuseEffectが発火しチカチカする）
  const paintRules = useMemo(
    () => (layerProps ? createPaintRules(layerProps.dataLayer) : []),
    [layerProps?.dataLayer]
  );

  if (layerProps) {
    const { url, layerName, dataLayer } = layerProps;
    return (
      <HazardBaseLayer
        defaultVisible={defaultVisible}
        layerName={layerName}
        dataLayer={dataLayer}
        url={url}
        attributeKey=""
        colorMap={EMPTY_COLOR_MAP}
        paintRules={paintRules}
        minzoom={MIN_ZOOM}
        maxzoom={MAX_ZOOM}
        opacity={LAYER_OPACITY}
        visible={visible}
      />
    );
  }
  return null;
};
